import { For, Show, type Component } from 'solid-js';

import { allocateAttribute, type AttributeKey } from '../net/protocol';
import type { GameStore } from '../state/store';
import { attributeRows, defenceRows, offenceRows, type StatRow } from './character';
import { BULK_SPEND, pointsForClick } from './bulkSpend';

/**
 * The character sheet: level, attributes, and what they add up to.
 *
 * Everything shown is the server's own arithmetic, delivered in the CHARACTER
 * frame. The sheet never computes a stat itself — if it did, the number a player
 * reads and the number the simulation uses could disagree, which is the one
 * thing a stat screen must never do.
 *
 * ── What left, and why ──────────────────────────────────────────────────────
 *
 * The paper doll and the `Carried` list. This panel used to be a stat screen, an
 * equipment screen and half an inventory at once, and the same items appeared here *and*
 * in the vault overlay in two different layouts, neither of which showed how much room
 * anything took.
 *
 * The doll moved to the bag panel because equipping is now a **drag** from the grid onto
 * a socket, and the two have to be on screen together — `menu` deliberately holds one
 * panel at a time, since every panel covers the canvas. That constraint produced the
 * Diablo arrangement rather than the arrangement being copied: `I` is the doll plus the
 * grid, `C` is the numbers. See `PaperDoll.tsx` and
 * `doc/grind/architecture/CONTAINERS-GRID-INVENTORY.md`.
 */
export const CharacterSheet: Component<{ store: GameStore }> = (props) => {
  const sheet = () => props.store.character();

  return (
    <Show when={props.store.menu() === 'character'}>
      <div
        class="sheet sheet--stats hud-interactive"
        data-testid="character-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Character"
      >
        <div class="plate plate--riveted sheet__panel">
          <header class="sheet__head">
            <h2 class="sheet__title">Character</h2>
            <Show when={sheet()?.progression}>
              {(prog) => (
                <div class="sheet__level" data-testid="character-level">
                  <span class="sheet__level-number">Level {prog().level}</span>
                  <div class="sheet__xp" title={`${prog().xpIntoLevel} / ${prog().xpForNext} XP`}>
                    <div
                      class="sheet__xp-fill"
                      style={{
                        width: `${
                          prog().xpForNext > 0
                            ? Math.min(100, (prog().xpIntoLevel / prog().xpForNext) * 100)
                            : 100
                        }%`,
                      }}
                    />
                  </div>
                </div>
              )}
            </Show>
            <button
              type="button"
              class="sheet__close"
              data-testid="character-close"
              onClick={() => props.store.closeMenu()}
              aria-label="Close"
            >
              ✕
            </button>
          </header>

          <Show
            when={sheet()}
            fallback={
              <p class="sheet__waiting" data-testid="character-waiting">
                Awaiting the server's reckoning of you.
              </p>
            }
          >
            {(c) => (
              <div class="sheet__body sheet__body--stats">
                {/* ── Attributes ──────────────────────────────────────── */}
                <section class="sheet__attributes" aria-label="Attributes">
                  <h3 class="sheet__heading">Attributes</h3>
                  <For each={attributeRows(c())}>
                    {(row) => (
                      <div class="attr" data-testid={`attr-${row.key}`}>
                        <div class="attr__line">
                          <span class="attr__label">{row.label}</span>
                          {/* The button's grid column exists whether or not there is a
                              point to spend, so the values do not shift sideways the
                              moment a level-up is consumed — see `.attr__line`. */}
                          <Show when={c().progression.attributePoints > 0} fallback={<span />}>
                            <button
                              type="button"
                              class="attr__spend"
                              data-testid={`allocate-${row.key}`}
                              title={`Spend a point on ${row.label} — hold Ctrl for ${BULK_SPEND}`}
                              onClick={(e) => {
                                // Ctrl (or Cmd) spends five, clamped to what is left. Five separate
                                // commands rather than a count on the wire — see `bulkSpend.ts`.
                                const n = pointsForClick(e, c().progression.attributePoints);
                                for (let i = 0; i < n; i++) {
                                  props.store.send(allocateAttribute(row.key as AttributeKey));
                                }
                              }}
                            >
                              +
                            </button>
                          </Show>
                          <span class="attr__value">
                            {row.total}
                            <Show when={row.gear !== 0}>
                              <span class="attr__gear">
                                {' '}
                                ({row.base}
                                {row.gear > 0 ? ' +' : ' '}
                                {row.gear})
                              </span>
                            </Show>
                          </span>
                        </div>
                        <p class="attr__effect">{row.effect}</p>
                      </div>
                    )}
                  </For>

                  <p class="sheet__points" data-testid="character-unspent">
                    <Show
                      when={c().progression.attributePoints > 0}
                      fallback={<span class="sheet__seam">No attribute points to spend.</span>}
                    >
                      <strong>{c().progression.attributePoints}</strong> attribute point
                      {c().progression.attributePoints === 1 ? '' : 's'} to spend
                    </Show>
                  </p>

                  {/* The gear is one key away, and saying so is what stops the split
                      from reading as a missing feature. */}
                  <p class="sheet__seam" data-testid="character-gear-hint">
                    Your gear and your bag are on <kbd class="codex__key">I</kbd>.
                  </p>
                </section>

                {/*
                  ── Derived, in two columns ──────────────────────────────

                  **Offence and defence are separate grid children, and that is the fix.**
                  They were one 765px column inside a 748px panel, so the sheet scrolled —
                  while the third of its three declared tracks stood empty, because only
                  two children existed to fill them. A 1120px panel held 654px of content
                  and a scrollbar.

                  Two lists, two columns: the panel fills its width and stops scrolling,
                  and the two halves can be read side by side rather than one under the
                  other.
                */}
                <section class="sheet__stats" aria-label="Offence">
                  <h3 class="sheet__heading">Offence</h3>
                  <StatList rows={offenceRows(c().derived)} />
                </section>

                <section class="sheet__stats" aria-label="Defence">
                  <h3 class="sheet__heading">Defence</h3>
                  <StatList rows={defenceRows(c().derived)} />

                  <p class="sheet__seam">
                    Dimmed figures are tallied from your gear but not yet read by the world — no
                    rule for them has been written down.
                  </p>
                </section>
              </div>
            )}
          </Show>
        </div>
      </div>
    </Show>
  );
};

/** A block of label/value rows, dimming the ones nothing reads yet. */
const StatList: Component<{ rows: StatRow[] }> = (props) => (
  <dl class="stats">
    <For each={props.rows}>
      {(row) => (
        <div class="stats__row" classList={{ 'stats__row--inert': !row.live }}>
          <dt>{row.label}</dt>
          <dd>{row.value}</dd>
        </div>
      )}
    </For>
  </dl>
);
